import { createFileRoute, Link } from "@tanstack/react-router";
import { DataError } from "@/components/data-error";
import { useEffect, useMemo, useState } from "react";
import { useCallsSince, useNow, hourFloorIso } from "@/hooks/use-calls-since";
import { useAuth } from "@/hooks/use-auth";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { formatDuration, formatWhen, callStatusLabel, isReallyLive } from "@/lib/calls";
import { CallIcon, CallStatus, isAnsweredStatus, isMissedStatus, customerNumber } from "@/components/call-bits";
import { Download, Search, Voicemail, X, ChevronRight } from "lucide-react";

export const Route = createFileRoute("/_app/calls/")({
  head: () => ({ meta: [{ title: "Call logs — EESWEB" }] }),
  component: CallsPage,
});

type CallRow = {
  id: string; started_at: string | null; direction: string; status: string;
  from_number: string | null; to_number: string | null; did_number: string | null;
  agent_name: string | null; caller_name: string | null; ivr_path: string | null;
  duration_sec: number | null; billable_sec: number | null; recording_available: boolean | null; is_live?: boolean | null;
};

// Owners think in outcomes, not Smartflo status codes: each option covers every code that means it.
const STATUS_GROUPS: Record<string, { label: string; codes: string[] }> = {
  answered: { label: "Answered", codes: ["answered", "completed"] },
  missed: { label: "Missed", codes: ["missed", "no_answer"] },
  busy: { label: "Busy", codes: ["busy"] },
  failed: { label: "Failed", codes: ["failed"] },
  cancelled: { label: "Caller hung up", codes: ["cancelled"] },
  in_progress: { label: "In progress", codes: ["ringing", "auto_attendant"] },
};

function dayHeading(iso: string | null): string {
  if (!iso) return "Unknown date";
  const d = new Date(iso);
  const today = new Date(); today.setHours(0, 0, 0, 0);
  const that = new Date(d); that.setHours(0, 0, 0, 0);
  const diff = Math.round((today.getTime() - that.getTime()) / 86_400_000);
  if (diff === 0) return "Today";
  if (diff === 1) return "Yesterday";
  return d.toLocaleDateString([], { weekday: "long", day: "numeric", month: "long" });
}

function CallsPage() {
  const { effectiveClientId } = useAuth();
  const [search, setSearch] = useState("");
  const [direction, setDirection] = useState<string>("all");
  const [status, setStatus] = useState<string>("all");
  const [days, setDays] = useState("7");
  // Render in pages so a busy month doesn't build thousands of rows at once; counts and export use everything.
  const PAGE = 200;
  const [shown, setShown] = useState(PAGE);

  // Live listener over the chosen range (subscribed from the top of the hour so it
  // doesn't re-subscribe every render); the exact cut-off is applied here.
  const now = useNow(60_000);
  const rangeMs = parseInt(days, 10) * 86_400_000;
  const { data: inRange, isLoading, error: callsError } = useCallsSince<CallRow>(effectiveClientId, hourFloorIso(rangeMs, now));
  const calls = useMemo(() => {
    const sinceIso = new Date(now - rangeMs).toISOString();
    return inRange?.filter((c) => c.started_at && c.started_at >= sinceIso && (direction === "all" || c.direction === direction));
  }, [inRange, now, rangeMs, direction]);

  const filtered = useMemo(() => {
    let rows = calls ?? [];
    if (status !== "all") rows = rows.filter((c) => STATUS_GROUPS[status]?.codes.includes(c.status));
    if (search) {
      const s = search.toLowerCase().replace(/\s+/g, "");
      rows = rows.filter((c) =>
        [c.from_number, c.to_number, c.did_number, c.agent_name, c.caller_name]
          .filter(Boolean).some((v) => String(v).toLowerCase().replace(/\s+/g, "").includes(s)),
      );
    }
    return rows;
  }, [calls, search, status]);

  const groups = useMemo(() => {
    const out: { heading: string; rows: CallRow[] }[] = [];
    for (const c of filtered.slice(0, shown)) {
      const h = dayHeading(c.started_at);
      if (out.at(-1)?.heading === h) out.at(-1)!.rows.push(c);
      else out.push({ heading: h, rows: [c] });
    }
    return out;
  }, [filtered, shown]);

  useEffect(() => setShown(PAGE), [search, direction, status, days]);
  const hasFilters = search !== "" || direction !== "all" || status !== "all";
  const clearFilters = () => { setSearch(""); setDirection("all"); setStatus("all"); };

  const exportCsv = () => {
    const rows = [
      ["Started", "Direction", "From", "To", "DID", "Agent", "Status", "Duration", "Billable", "Recording"],
      ...filtered.map((c) => [
        c.started_at ?? "", c.direction, c.from_number ?? "", c.to_number ?? "",
        c.did_number ?? "", c.agent_name ?? "", c.status, c.duration_sec ?? 0,
        c.billable_sec ?? 0, c.recording_available ? "yes" : "no",
      ]),
    ];
    const csv = rows.map((r) => r.map((v) => `"${String(v).replace(/"/g, '""')}"`).join(",")).join("\n");
    const blob = new Blob([csv], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url; a.download = `calls-${new Date().toISOString().slice(0, 10)}.csv`;
    a.click(); URL.revokeObjectURL(url);
  };

  return (
    <div className="mx-auto max-w-6xl space-y-5">
      <DataError error={callsError} />
      <div className="flex flex-wrap items-end justify-between gap-x-4 gap-y-1">
        <div>
          <h1 className="font-display text-2xl md:text-3xl font-semibold tracking-tight">Call logs</h1>
          <p className="text-sm text-muted-foreground">Every call on your Smartflo number, newest first.</p>
        </div>
        <Button variant="outline" size="sm" onClick={exportCsv} disabled={filtered.length === 0}>
          <Download className="size-4" /> Export CSV
        </Button>
      </div>

      {/* Filters */}
      <div className="flex flex-col gap-2 md:flex-row md:items-center">
        <div className="relative md:flex-1">
          <Search className="pointer-events-none absolute left-3 top-1/2 size-4 -translate-y-1/2 text-muted-foreground" />
          <Input
            type="search"
            inputMode="search"
            placeholder="Search by number or agent"
            aria-label="Search calls"
            className="h-10 bg-card pl-9"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </div>
        <div className="grid grid-cols-2 gap-2 md:flex">
          <Select value={status} onValueChange={setStatus}>
            <SelectTrigger className="h-10 bg-card md:w-40" aria-label="Filter by outcome"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All outcomes</SelectItem>
              {Object.entries(STATUS_GROUPS).map(([k, g]) => <SelectItem key={k} value={k}>{g.label}</SelectItem>)}
            </SelectContent>
          </Select>
          <Select value={direction} onValueChange={setDirection}>
            <SelectTrigger className="h-10 bg-card md:w-36" aria-label="Filter by direction"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">In &amp; out</SelectItem>
              <SelectItem value="inbound">Incoming</SelectItem>
              <SelectItem value="outbound">Outgoing</SelectItem>
              <SelectItem value="internal">Internal</SelectItem>
            </SelectContent>
          </Select>
          <Select value={days} onValueChange={setDays}>
            <SelectTrigger className="col-span-2 h-10 bg-card md:w-36" aria-label="Date range"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="1">Last 24 hours</SelectItem>
              <SelectItem value="7">Last 7 days</SelectItem>
              <SelectItem value="30">Last 30 days</SelectItem>
              <SelectItem value="90">Last 90 days</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>

      <div className="flex items-center justify-between text-sm text-muted-foreground">
        <span className="tabular">
          {isLoading ? "Loading calls…" : `${filtered.length} ${filtered.length === 1 ? "call" : "calls"}`}
        </span>
        {hasFilters && (
          <button onClick={clearFilters} className="inline-flex items-center gap-1 rounded-md px-1.5 py-0.5 text-xs font-medium text-primary hover:bg-primary/10">
            <X className="size-3.5" /> Clear filters
          </button>
        )}
      </div>

      <div className="overflow-hidden rounded-2xl border bg-card shadow-soft">
        {/* Column headings (desktop) */}
        <div className="hidden grid-cols-[minmax(0,1.6fr)_minmax(0,1.3fr)_7rem_6rem_8.5rem_1.25rem] gap-4 border-b bg-muted/40 px-5 py-2.5 text-xs font-medium text-muted-foreground lg:grid">
          <span>Caller</span><span>Handled by</span><span>Time</span><span className="text-right">Talk time</span><span>Outcome</span><span />
        </div>

        {isLoading && (
          <div className="divide-y">
            {Array.from({ length: 6 }).map((_, i) => (
              <div key={i} className="flex items-center gap-3 px-5 py-3.5">
                <Skeleton className="size-4 rounded-full" />
                <div className="flex-1 space-y-1.5"><Skeleton className="h-3.5 w-32" /><Skeleton className="h-3 w-20" /></div>
                <Skeleton className="h-3.5 w-14" />
              </div>
            ))}
          </div>
        )}

        {!isLoading && !callsError && filtered.length === 0 && (
          <div className="flex flex-col items-center gap-2 px-6 py-16 text-center">
            <span className="grid size-10 place-items-center rounded-full bg-muted text-muted-foreground"><Search className="size-[18px]" /></span>
            <p className="text-sm font-medium">{hasFilters ? "No calls match these filters" : "No calls in this period"}</p>
            <p className="max-w-xs text-sm text-muted-foreground">
              {hasFilters ? "Try a different number, outcome or date range." : "Calls appear here as soon as Smartflo reports them."}
            </p>
            {hasFilters && <Button variant="outline" size="sm" className="mt-2" onClick={clearFilters}>Clear filters</Button>}
          </div>
        )}

        {!isLoading && groups.map((g) => (
          <section key={g.heading} aria-label={g.heading}>
            <h2 className="border-b bg-muted/30 px-5 py-2 text-xs font-semibold text-muted-foreground">
              {g.heading} <span className="font-normal tabular">· {g.rows.length}</span>
            </h2>
            <ul className="divide-y">
              {g.rows.map((c) => {
                const live = isReallyLive(c, Date.now());
                // Talk time only exists once the call has ended.
                const answered = isAnsweredStatus(c.status) && !live;
                const handledBy = c.agent_name ?? (c.ivr_path ? `IVR ${c.ivr_path.replace(/>/g, " › ")}` : null);
                return (
                  <li key={c.id}>
                    <Link
                      to="/calls/$id"
                      params={{ id: c.id }}
                      className="group grid grid-cols-[auto_minmax(0,1fr)_auto] items-center gap-x-3 px-5 py-3 transition-colors hover:bg-muted/50 focus-visible:bg-muted/50 focus-visible:outline-none lg:grid-cols-[minmax(0,1.6fr)_minmax(0,1.3fr)_7rem_6rem_8.5rem_1.25rem] lg:gap-4"
                    >
                      {/* Caller */}
                      <div className="contents lg:flex lg:min-w-0 lg:items-center lg:gap-3">
                        <CallIcon direction={c.direction} status={c.status} />
                        <div className="min-w-0">
                          <div className="flex items-center gap-1.5">
                            <span className="truncate font-medium tabular">{c.caller_name ?? customerNumber(c) ?? "Unknown number"}</span>
                            {c.recording_available && <Voicemail className="size-3.5 shrink-0 text-muted-foreground" aria-label="Recording available" />}
                          </div>
                          {/* Phone: secondary line carries outcome + who handled it */}
                          <div className="truncate text-xs text-muted-foreground lg:hidden">
                            {live
                              ? <span className="text-success">{c.agent_name ? `On a call with ${c.agent_name}` : callStatusLabel(c.status)}</span>
                              : answered
                              ? (handledBy ?? "Answered")
                              : <span className={isMissedStatus(c.status) ? "text-destructive" : ""}>{callStatusLabel(c.status)}</span>}
                          </div>
                          {c.caller_name && customerNumber(c) && <div className="hidden truncate text-xs text-muted-foreground tabular lg:block">{customerNumber(c)}</div>}
                        </div>
                      </div>
                      {/* Phone: time + duration on the right */}
                      <div className="text-right lg:hidden">
                        <div className="text-xs text-muted-foreground tabular">{formatWhen(c.started_at)}</div>
                        {answered && <div className="text-xs font-medium tabular">{formatDuration(c.billable_sec)}</div>}
                      </div>
                      {/* Desktop columns */}
                      <span className="hidden truncate text-sm text-muted-foreground lg:block">{handledBy ?? "—"}</span>
                      <span className="hidden text-sm text-muted-foreground tabular lg:block">{formatWhen(c.started_at)}</span>
                      <span className="hidden text-right text-sm tabular lg:block">{answered ? formatDuration(c.billable_sec) : "—"}</span>
                      <span className="hidden lg:block"><CallStatus status={c.status} live={live} /></span>
                      <ChevronRight className="hidden size-4 text-muted-foreground/60 transition-transform group-hover:translate-x-0.5 lg:block" />
                    </Link>
                  </li>
                );
              })}
            </ul>
          </section>
        ))}
        {!isLoading && filtered.length > shown && (
          <button onClick={() => setShown((n) => n + PAGE)} className="w-full border-t px-5 py-3 text-sm font-medium text-primary hover:bg-muted/40">
            Show {Math.min(PAGE, filtered.length - shown)} more <span className="font-normal text-muted-foreground tabular">({shown} of {filtered.length} shown)</span>
          </button>
        )}
      </div>
    </div>
  );
}
